// ---------------------------------------------------------------------------
// Slideshow Express — real-typeface loading for the Manage Styles font picker
// ---------------------------------------------------------------------------
// The server compositor burns slide text using bundled TTFs (server/assets/
// fonts) or an uploaded custom font. This loader fetches those exact files
// into the browser via the FontFace API so the dropdown options and the
// preview box render in the true typeface instead of the rough cssFallback.
//
// Bundled fonts come from GET /api/slideshow-fonts/:file (same-origin, long
// cache). Uploaded custom fonts live on R2 which sends no CORS headers — and
// browsers always fetch fonts in CORS mode — so absolute URLs are routed
// through the existing same-origin /api/brand-font proxy (dev /uploads URLs
// are already same-origin and load directly).
//
// Loading is fail-open: while a font is in flight (or if it fails), callers
// keep the cssFallback stack, then upgrade once loaded. Loaded families are
// cached module-wide so reopening the dialog never re-fetches.
import { useEffect, useState } from "react";
import {
  SLIDESHOW_BUNDLED_FONTS,
  getSlideshowBundledFont,
  type SlideshowCustomFont,
} from "@shared/slideshow-text-settings";

// Families whose FontFace has finished loading and been added to document.fonts.
const loadedFamilies = new Set<string>();
// Families we've already started loading (in flight or failed) — never retry a
// family within a session; a failure simply leaves the fallback in place.
const startedFamilies = new Set<string>();
// Subscribers re-rendered whenever any font finishes loading.
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

function loadFamily(cssFamily: string, src: string): void {
  if (startedFamilies.has(cssFamily)) return;
  startedFamilies.add(cssFamily);
  try {
    if (typeof FontFace === "undefined" || !document.fonts) return;
    const face = new FontFace(cssFamily, `url("${src}")`);
    face
      .load()
      .then((f) => {
        document.fonts.add(f);
        loadedFamilies.add(cssFamily);
        notify();
      })
      .catch(() => {
        // Failed load — option keeps its cssFallback stack.
      });
  } catch {
    // FontFace unsupported / bad URL — fallback stays.
  }
}

function bundledCssFamily(key: string): string {
  return `ss-bundled-${key}`;
}

// Custom fonts are keyed by URL (each upload gets a unique R2 key) so two
// different uploads never collide under one family name.
function hashStr(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

function customCssFamily(url: string): string {
  return `ss-custom-${hashStr(url)}`;
}

function customFontSrc(url: string): string {
  return /^https?:\/\//i.test(url)
    ? `/api/brand-font?url=${encodeURIComponent(url)}`
    : url;
}

/** Kick off loading of all bundled slideshow fonts plus the given uploaded
 *  custom font (if any), and re-render the caller as each one lands. Returns
 *  helpers that produce the best-available CSS font-family stack per option:
 *  the real typeface once loaded, the cssFallback stack until then. */
export function useSlideshowPickerFonts(
  customFont: SlideshowCustomFont | null | undefined,
  enabled = true,
): {
  bundledFamily: (key: string) => string;
  customFamily: () => string;
} {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const listener = () => setTick((t) => t + 1);
    listeners.add(listener);
    for (const f of SLIDESHOW_BUNDLED_FONTS) {
      if (f.file) {
        loadFamily(bundledCssFamily(f.key), `/api/slideshow-fonts/${f.file}`);
      }
    }
    return () => {
      listeners.delete(listener);
    };
  }, [enabled]);

  const customUrl = customFont?.url || "";
  useEffect(() => {
    if (enabled && customUrl) {
      loadFamily(customCssFamily(customUrl), customFontSrc(customUrl));
    }
  }, [enabled, customUrl]);

  const bundledFamily = (key: string): string => {
    const font = getSlideshowBundledFont(key);
    const fallback = font?.cssFallback ?? "system-ui, sans-serif";
    if (!font?.file) return fallback;
    const cssFamily = bundledCssFamily(font.key);
    return loadedFamilies.has(cssFamily) ? `'${cssFamily}', ${fallback}` : fallback;
  };

  const customFamily = (): string => {
    if (!customUrl) return "sans-serif";
    const cssFamily = customCssFamily(customUrl);
    return loadedFamilies.has(cssFamily) ? `'${cssFamily}', sans-serif` : "sans-serif";
  };

  return { bundledFamily, customFamily };
}
